import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createBlock as _createBlock, unref as _unref, normalizeClass as _normalizeClass, Teleport as _Teleport, Fragment as _Fragment, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-4306bc64"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "screen-wrapper" }
const _hoisted_2 = { class: "is-flex is-align-items-center is-justify-content-space-between mb-5" }
const _hoisted_3 = {
  key: 0,
  class: "mb-0"
}
const _hoisted_4 = {
  key: 1,
  class: "mb-0"
}
const _hoisted_5 = { class: "form" }
const _hoisted_6 = { class: "field" }
const _hoisted_7 = { class: "field" }
const _hoisted_8 = { class: "field" }
const _hoisted_9 = { class: "field" }
const _hoisted_10 = { class: "field" }
const _hoisted_11 = { class: "field" }

import { useRoute, useRouter } from 'vue-router'
import { computed, onMounted, onUnmounted, ref, toRaw } from 'vue'
import { toHex } from 'web3-utils'

import { Input, Error, Label } from '@/popup/components/shared/Form/Form'
import {
  urlValidator,
  wsValidator,
  localhostValidator,
  nonSpaceCharacterValidator,
  numberOrHexValidator,
} from '@/popup/components/shared/Form/validators'
import IconButton from '@/popup/components/shared/IconButton.vue'
import Button from '@/popup/components/shared/Button.vue'
import { GlobalDatabase, saveLastState } from '@/popup/storage/Database'
import { useAppStore } from '@/popup/store/AppStore'
import NetworkDeleteModal from '@/popup/components/Modal/NetworkDelete.vue'
import getUniqueId from '@/shared/getUniqueId'
import DropdownMultiple from '@/popup/components/shared/Dropdown/DropdownMultiple.vue'
import Notification from '@/popup/components/shared/Notification.vue'
import { InputError } from '@/popup/components/shared/Form/FormTypes'
import {
  NetworkInfoDBSchema,
  RelayServiceDBSchema,
} from '@/shared/Database/UPSettings/models'
import { RelayerList } from '@/shared/models/Relayer'
import { useApprovalsStore } from '@/popup/store/ApprovalsStore'
import { redirectAfterApproval } from '@/popup/utils/redirectAfterApproval'
import { useGlobals } from '@/popup/compositions/useGlobals'
import { InternalMethods } from '@/shared/apis/messages'
import ModalTransition from '@/popup/components/shared/Modal/ModalTransition.vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'AddNetwork',
  setup(__props) {

const network = ref({
  name: '',
  http: { url: '' },
  ws: { url: '' },
  explorer: { url: '' },
  chainId: '',
} as NetworkInfoDBSchema)
const route = useRoute()

const networkId = route.params.networkId as string | undefined
const approvalsStore = useApprovalsStore()
const approval = networkId ? approvalsStore.getApproval(networkId) : undefined

const name = ref<InstanceType<typeof Input>>()
const rpcUrl = ref<InstanceType<typeof Input>>()
const websocketUrl = ref<InstanceType<typeof Input>>()
const chainId = ref<InstanceType<typeof Input>>()
const explorerUrl = ref<InstanceType<typeof Input>>()

const nameErrors = ref<InputError[]>()
const rpcUrlErrors = ref<InputError[]>()
const websocketUrlErrors = ref<InputError[]>()
const explorerUrlErrors = ref<InputError[]>()
const chainIdErrors = ref<InputError[]>()
const focus = ref(false)
const router = useRouter()
const appStore = useAppStore()
const isDeleteModalOpen = ref(false)
const isEdit = !!networkId
const allRelayers = ref<RelayServiceDBSchema[]>([])
const networkRelayers = ref<RelayerList[]>([])

onMounted(async () => {
  // recover from saved state
  if (appStore.lastPopupState?.network) {
    network.value = appStore.lastPopupState.network
  } else if (isEdit) {
    const dbNetwork = await GlobalDatabase.getNetwork(networkId)

    if (dbNetwork) {
      network.value = dbNetwork
    } else if (approval) {
      network.value = approval.requestData as NetworkInfoDBSchema
    }
  }

  allRelayers.value = await GlobalDatabase.getAllRelayers()

  networkRelayers.value = appStore.lastPopupState?.networkRelayers
    ? JSON.parse(appStore.lastPopupState.networkRelayers)
    : allRelayers.value
        .filter(relayer => relayer.chainIds.includes(network.value.chainId))
        .map(relayer => ({ ...relayer, dbAction: 'none' }))
  focus.value = true
})

const canSubmit = computed(
  () =>
    !!network.value?.name &&
    !!network.value?.http.url &&
    !!network.value?.ws.url &&
    !!network.value?.chainId &&
    !nameErrors.value?.length &&
    !rpcUrlErrors.value?.length &&
    !websocketUrlErrors.value?.length &&
    !chainIdErrors.value?.length &&
    !explorerUrlErrors.value?.length
)

const validate = () => {
  name.value?.validate(network.value?.name)
  rpcUrl.value?.validate(network.value?.http.url)
  websocketUrl.value?.validate(network.value?.ws.url)
  chainId.value?.validate(network.value?.chainId)
  explorerUrl.value?.validate(network.value?.explorer?.url || '')
}

const save = async () => {
  validate()
  if (!canSubmit.value) {
    return
  }

  const updatedNetwork: NetworkInfoDBSchema = {
    ...toRaw(network.value),
    isCustom: true,
  }

  if (!isEdit) {
    updatedNetwork.id = getUniqueId()
  }

  // since we do multiple updates in DB we wrap into transaction
  await GlobalDatabase.transaction(
    'rw',
    'relayers',
    'networks',

    async () => {
      await GlobalDatabase.put('networks', updatedNetwork)

      for await (const relayer of toRaw(networkRelayers.value)) {
        if (relayer.dbAction) {
          ;({
            add: async () => {
              delete relayer.dbAction
              const updatedRelayer = {
                ...relayer,
                chainIds: [...relayer.chainIds, updatedNetwork.chainId],
              }

              await GlobalDatabase.put('relayers', updatedRelayer)
            },
            remove: async () => {
              delete relayer.dbAction
              const updatedRelayer = {
                ...relayer,
                chainIds: relayer.chainIds.filter(
                  chainId => chainId !== updatedNetwork.chainId
                ),
              }

              await GlobalDatabase.put('relayers', updatedRelayer)
            },
            none: () => {
              return
            },
          })[relayer.dbAction]()
        }
      }
    }
  )
  if (approval) {
    useGlobals().sendMessage({
      method: InternalMethods.ADD_NETWORK,
      payload: { approvalId: approval.id, chainId: updatedNetwork.chainId },
    })
  }
  router.push('/settings')
}

const onCancel = () => {
  if (approval?.id) {
    approvalsStore.deleteApproval(approval.id)
    useGlobals().sendMessage({
      method: InternalMethods.ADD_NETWORK,
      payload: { approvalId: approval.id },
    })
    redirectAfterApproval(
      router,
      approvalsStore.nonStashedApprovals.length,
      appStore.lastPopupState?.path
    )
  }
  router.push('/settings')
}

const toggleModal = () => {
  isDeleteModalOpen.value = !isDeleteModalOpen.value
}

const deleteNetwork = async () => {
  if (isEdit) {
    await GlobalDatabase.removeNetwork(networkId)
  }
  router.push('/settings')
}

const saveState = async () => {
  await saveLastState({
    path: router.currentRoute.value.path,
    network: toRaw(network.value),
    networkRelayers: JSON.stringify(toRaw(networkRelayers.value)),
  })
}

const onAddRelayer = async (relayer: RelayServiceDBSchema) => {
  networkRelayers.value.push({ ...relayer, dbAction: 'add' })
  await saveState()
}

const onRemoveRelayer = async (relayer: RelayServiceDBSchema) => {
  const networkRelayer = networkRelayers.value.find(
    dbRelayer => dbRelayer.id == relayer.id
  )

  if (networkRelayer && networkRelayer.dbAction === 'add') {
    const index = networkRelayers.value.indexOf(networkRelayer)
    networkRelayers.value.splice(index, 1)
  } else if (networkRelayer && networkRelayer.dbAction === 'none') {
    const index = networkRelayers.value.indexOf(networkRelayer)
    networkRelayers.value[index] = {
      ...networkRelayer,
      dbAction: 'remove',
    }
  } else {
    networkRelayers.value.push({ ...relayer, dbAction: 'remove' })
  }

  saveState()
}

const networkRelayersList = computed(() =>
  networkRelayers.value.filter(
    networkRelayer =>
      networkRelayer.dbAction === 'none' || networkRelayer.dbAction === 'add'
  )
)

onUnmounted(() => {
  saveLastState({
    network: undefined,
    networkRelayers: undefined,
  })
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        isEdit
          ? (_openBlock(), _createElementBlock("h3", _hoisted_3, _toDisplayString(_ctx.$t('network.edit.heading')), 1 /* TEXT */))
          : (_openBlock(), _createElementBlock("h3", _hoisted_4, _toDisplayString(_ctx.$t('network.add.heading')), 1 /* TEXT */)),
        _createVNode(IconButton, { onOnClick: onCancel })
      ]),
      (!isEdit)
        ? (_openBlock(), _createBlock(Notification, {
            key: 0,
            class: "mb-4"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('network.add.warning')), 1 /* TEXT */)
            ]),
            _: 1 /* STABLE */
          }))
        : _createCommentVNode("v-if", true),
      _createElementVNode("div", _hoisted_5, [
        _createElementVNode("div", null, [
          _createElementVNode("div", _hoisted_6, [
            _createVNode(_unref(Label), {
              label: _ctx.$t('network.add.name_label')
            }, null, 8 /* PROPS */, ["label"]),
            _createVNode(_unref(Input), {
              name: "name",
              ref_key: "name",
              ref: name,
              value: network.value.name,
              placeholder: _ctx.$t('network.add.name_placeholder'),
              autofocus: focus.value,
              "is-required": true,
              "extra-classes": "input",
              validators: [_unref(nonSpaceCharacterValidator)],
              errors: nameErrors.value,
              onOnUpdate: _cache[0] || (_cache[0] = value => (network.value.name = value) && saveState()),
              onOnErrors: _cache[1] || (_cache[1] = newErrors => (nameErrors.value = newErrors))
            }, null, 8 /* PROPS */, ["value", "placeholder", "autofocus", "validators", "errors"]),
            (nameErrors.value?.length)
              ? (_openBlock(), _createBlock(_unref(Error), {
                  key: 0,
                  error: nameErrors.value[0]
                }, null, 8 /* PROPS */, ["error"]))
              : _createCommentVNode("v-if", true)
          ]),
          _createElementVNode("div", _hoisted_7, [
            _createVNode(_unref(Label), {
              label: _ctx.$t('network.add.rpc_url_label')
            }, null, 8 /* PROPS */, ["label"]),
            _createVNode(_unref(Input), {
              name: "rpcUrl",
              ref_key: "rpcUrl",
              ref: rpcUrl,
              value: network.value.http.url,
              placeholder: _ctx.$t('network.add.rpc_url_placeholder'),
              errors: rpcUrlErrors.value,
              validators: [_unref(urlValidator), _unref(localhostValidator)],
              "is-required": true,
              "extra-classes": "input",
              onOnUpdate: _cache[2] || (_cache[2] = value => (network.value.http.url = value) && saveState()),
              onOnErrors: _cache[3] || (_cache[3] = newErrors => (rpcUrlErrors.value = newErrors))
            }, null, 8 /* PROPS */, ["value", "placeholder", "errors", "validators"]),
            (rpcUrlErrors.value?.length)
              ? (_openBlock(), _createBlock(_unref(Error), {
                  key: 0,
                  error: rpcUrlErrors.value[0]
                }, null, 8 /* PROPS */, ["error"]))
              : _createCommentVNode("v-if", true)
          ]),
          _createElementVNode("div", _hoisted_8, [
            _createVNode(_unref(Label), {
              label: _ctx.$t('network.add.websocket_url_label')
            }, null, 8 /* PROPS */, ["label"]),
            _createVNode(_unref(Input), {
              name: "websocketUrl",
              ref_key: "websocketUrl",
              ref: websocketUrl,
              value: network.value.ws.url,
              placeholder: _ctx.$t('network.add.websocket_url_placeholder'),
              errors: websocketUrlErrors.value,
              validators: [_unref(wsValidator), _unref(localhostValidator)],
              "is-required": true,
              "extra-classes": "input",
              onOnUpdate: _cache[4] || (_cache[4] = value => (network.value.ws.url = value) && saveState()),
              onOnErrors: _cache[5] || (_cache[5] = newErrors => (websocketUrlErrors.value = newErrors))
            }, null, 8 /* PROPS */, ["value", "placeholder", "errors", "validators"]),
            (websocketUrlErrors.value?.length)
              ? (_openBlock(), _createBlock(_unref(Error), {
                  key: 0,
                  error: websocketUrlErrors.value[0]
                }, null, 8 /* PROPS */, ["error"]))
              : _createCommentVNode("v-if", true)
          ]),
          _createElementVNode("div", _hoisted_9, [
            _createVNode(_unref(Label), {
              label: _ctx.$t('network.add.chain_id_label')
            }, null, 8 /* PROPS */, ["label"]),
            _createVNode(_unref(Input), {
              name: "chainId",
              ref_key: "chainId",
              ref: chainId,
              value: network.value.chainId,
              placeholder: _ctx.$t('network.add.chain_id_placeholder'),
              errors: chainIdErrors.value,
              validators: [_unref(numberOrHexValidator)],
              "is-required": true,
              "extra-classes": "input",
              class: "is-family-monospace",
              onOnUpdate: _cache[6] || (_cache[6] = 
              value => (network.value.chainId = _unref(toHex)(value)) && saveState()
            ),
              onOnErrors: _cache[7] || (_cache[7] = newErrors => (chainIdErrors.value = newErrors))
            }, null, 8 /* PROPS */, ["value", "placeholder", "errors", "validators"]),
            (chainIdErrors.value?.length)
              ? (_openBlock(), _createBlock(_unref(Error), {
                  key: 0,
                  error: chainIdErrors.value[0]
                }, null, 8 /* PROPS */, ["error"]))
              : _createCommentVNode("v-if", true)
          ]),
          _createElementVNode("div", _hoisted_10, [
            _createVNode(_unref(Label), {
              label: _ctx.$t('network.add.explorer_url_label')
            }, null, 8 /* PROPS */, ["label"]),
            _createVNode(_unref(Input), {
              name: "explorerUrl",
              ref_key: "explorerUrl",
              ref: explorerUrl,
              value: network.value.explorer?.url,
              placeholder: _ctx.$t('network.add.explorer_url_placeholder'),
              errors: explorerUrlErrors.value,
              validators: [_unref(urlValidator), _unref(localhostValidator)],
              "extra-classes": "input",
              onOnUpdate: _cache[8] || (_cache[8] = 
              value =>
                (network.value.explorer
                  ? (network.value.explorer.url = value)
                  : undefined) && saveState()
            ),
              onOnErrors: _cache[9] || (_cache[9] = newErrors => (explorerUrlErrors.value = newErrors))
            }, null, 8 /* PROPS */, ["value", "placeholder", "errors", "validators"]),
            (explorerUrlErrors.value?.length)
              ? (_openBlock(), _createBlock(_unref(Error), {
                  key: 0,
                  error: explorerUrlErrors.value[0]
                }, null, 8 /* PROPS */, ["error"]))
              : _createCommentVNode("v-if", true)
          ]),
          _createElementVNode("div", _hoisted_11, [
            _createVNode(_unref(Label), {
              label: _ctx.$t('network.add.relayers_label')
            }, null, 8 /* PROPS */, ["label"]),
            _createVNode(DropdownMultiple, {
              selected: networkRelayersList.value,
              options: allRelayers.value,
              "option-id": "id",
              "option-label": "name",
              "is-open-up": true,
              onOnAddOption: onAddRelayer,
              onOnRemoveOption: onRemoveRelayer
            }, null, 8 /* PROPS */, ["selected", "options"])
          ])
        ]),
        _createElementVNode("div", {
          class: _normalizeClass(["buttons mt-5", { 'three-buttons': isEdit }])
        }, [
          isEdit
            ? (_openBlock(), _createBlock(Button, {
                key: 0,
                variant: "secondary",
                "custom-class": "text-red-55 border-red-55 ",
                onClick: toggleModal
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('network.delete.trigger')), 1 /* TEXT */)
                ]),
                _: 1 /* STABLE */
              }))
            : _createCommentVNode("v-if", true),
          _createVNode(Button, {
            variant: "secondary",
            onClick: onCancel
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('network.add.close')), 1 /* TEXT */)
            ]),
            _: 1 /* STABLE */
          }),
          _createVNode(Button, {
            variant: "primary",
            "is-full-width": true,
            type: "submit",
            disabled: !canSubmit.value,
            "data-testid": "add-network-button",
            onClick: save
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t(`network.${isEdit ? 'edit' : 'add'}.submit`)), 1 /* TEXT */)
            ]),
            _: 1 /* STABLE */
          }, 8 /* PROPS */, ["disabled"])
        ], 2 /* CLASS */)
      ])
    ]),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      _createVNode(ModalTransition, null, {
        default: _withCtx(() => [
          (isDeleteModalOpen.value)
            ? (_openBlock(), _createBlock(NetworkDeleteModal, {
                key: 0,
                "network-name": network.value.name,
                "on-confirm": deleteNetwork,
                "on-cancel": toggleModal
              }, null, 8 /* PROPS */, ["network-name"]))
            : _createCommentVNode("v-if", true)
        ]),
        _: 1 /* STABLE */
      })
    ]))
  ], 64 /* STABLE_FRAGMENT */))
}
}

})